<script setup lang="ts">
import { computed } from 'vue';

export interface Props {
  isOver?: boolean;
  isDisabled?: boolean;
  supportedFormats?: string[];
  maxFileSizeMb?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isOver: false,
  isDisabled: false,
  supportedFormats: () => ['pdf', 'png', 'jpg', 'jpeg'],
  maxFileSizeMb: 25,
});

const emit = defineEmits<{
  open: [];
}>();

function handleOpenClick(): void {
  if (props.isDisabled) return;
  emit('open');
}

const supportedFormatsLabel = computed(() => {
  if (!props.supportedFormats.length) {
    return 'Supported formats: any';
  }
  const formats = props.supportedFormats.map((format) => format.toUpperCase());
  return `Supported formats: ${formats.join(', ')}`;
});

const maxFileSizeLabel = computed(() => {
  return `Maximum size: ${props.maxFileSizeMb}MB`;
});
</script>

<template>
  <div :class="$style.dropZoneArea" data-testid="dropZoneArea">
    <div
      :class="[
        $style.zone,
        {
          [$style.disabled]: props.isDisabled,
          [$style.over]: props.isOver && !props.isDisabled,
        },
      ]"
    >
      <v-icon
        icon="mdi-cloud-upload"
        :class="[
          $style.icon,
          {
            [$style.disabledText]: props.isDisabled,
          },
        ]"
      />
      <div :class="$style.text">
        <span
          :class="{
            [$style.disabledText]: props.isDisabled,
          }"
        >
          Drag & drop or
        </span>
        <span
          :class="[
            $style.chooseFileLink,
            {
              [$style.disabledText]: props.isDisabled,
            },
          ]"
          @click="handleOpenClick"
          >Choose file</span
        >
        <span
          :class="{
            [$style.disabledText]: props.isDisabled,
          }"
        >
          to upload
        </span>
      </div>
    </div>
    <div :class="$style.meta">
      <span>{{ supportedFormatsLabel }}</span>
      <span>{{ maxFileSizeLabel }}</span>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.dropZoneArea {
  width: 100%;
}

  .zone {
    border: 1px dashed colors.$color-brand-300;
    border-radius: 8px;
    background-color: colors.$color-gray-50;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px 16px;
    gap: 8px;
    min-height: 74px;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }
  .over {
    background-color: colors.$color-brand-50;
    border-color: colors.$color-brand-500;
    border-style: solid;
  }
  .disabled {
    border-color: colors.$color-text-tertiary;
    cursor: not-allowed;
  }
    .disabledText {
      color: colors.$color-text-tertiary;
    }
    .icon {
      font-size: 32px;
      color: colors.$color-text-secondary;
      transition: color 0.15s ease;

      .over & {
        color: colors.$color-brand-500;
      }
    }
    .text {
      font-size: 14px;
      color: colors.$color-text-secondary;
    }
    .chooseFileLink {
      color: colors.$color-brand-500;
      text-decoration: underline;
      cursor: pointer;

      &:hover {
        text-decoration: none;
      }
    }
  .meta {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 12px;
    color: colors.$color-text-tertiary;
  }
</style>
